/**
 * PaymentHistoryGrid - Shared component for displaying payment history by bureau
 * Shows last 12 months by default with option to expand to full history
 *
 * THEME-AWARE: Supports both light and dark modes with proper styling
 *
 * Status codes: OK, 30, 60, 90, 120, CO, -
 * Missing / unknown values render "-" — never defaulted to OK.
 */

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  BUREAU_NAMES,
  generateMonthColumnsFromData,
  getStatusForBureauMonth,
  normalizePaymentStatus,
  parsePaymentHistory,
} from '../../utils/paymentHistoryUtils';

const BUREAUS = BUREAU_NAMES;

const getTheme = (darkMode) => (darkMode ? {
  card: { bg: 'rgba(10,34,25,.74)', border: 'rgba(142,218,182,.12)' },
  header: { bg: 'rgba(255,255,255,.04)' },
  row: { bg: 'rgba(255,255,255,.02)', alt: 'rgba(255,255,255,.028)' },
  lineSoft: 'rgba(142,218,182,.08)',
  text: { primary: '#EAF7F0', secondary: '#C7DDD0', muted: '#93AE9F' },
  legend: { bg: 'transparent' },
  ok: { bg: 'rgba(15,172,108,.12)', border: 'rgba(15,172,108,.18)', color: '#8FE0B7' },
  d30: { bg: 'rgba(234,178,58,.14)', border: 'rgba(234,178,58,.18)', color: '#F0C160' },
  d60: { bg: 'rgba(234,128,58,.16)', border: 'rgba(234,128,58,.18)', color: '#F1A46A' },
  d90: { bg: 'rgba(239,68,68,.12)', border: 'rgba(239,68,68,.18)', color: '#F27A7A' },
  co: { bg: 'rgba(67,78,101,.30)', border: 'rgba(67,78,101,.24)', color: '#EAF0FF' },
  na: { bg: 'rgba(255,255,255,.05)', border: 'rgba(255,255,255,.06)', color: '#93AE9F' },
} : {
  card: { bg: 'rgba(255,255,255,.76)', border: 'rgba(15,172,108,.10)' },
  header: { bg: 'rgba(255,255,255,.60)' },
  row: { bg: 'rgba(255,255,255,.52)', alt: 'rgba(247,252,249,.74)' },
  lineSoft: 'rgba(15,172,108,.08)',
  text: { primary: '#111827', secondary: '#4B5563', muted: '#6B7280' },
  legend: { bg: 'transparent' },
  ok: { bg: 'rgba(15,172,108,.10)', border: 'rgba(15,172,108,.16)', color: '#0B8C58' },
  d30: { bg: 'rgba(234,178,58,.14)', border: 'rgba(234,178,58,.18)', color: '#C98A1B' },
  d60: { bg: 'rgba(234,128,58,.16)', border: 'rgba(234,128,58,.18)', color: '#D9823A' },
  d90: { bg: 'rgba(239,68,68,.12)', border: 'rgba(239,68,68,.18)', color: '#E45757' },
  co: { bg: 'rgba(67,78,101,.30)', border: 'rgba(67,78,101,.24)', color: '#EAF0FF' },
  na: { bg: 'rgba(17,24,39,.04)', border: 'rgba(17,24,39,.06)', color: '#6B7280' },
});

const getStatusStyle = (status, theme) => {
  switch (status) {
    case 'OK':
      return theme.ok;
    case '30':
      return theme.d30;
    case '60':
      return theme.d60;
    case '90':
    case '120':
    case '150':
    case '180':
      return theme.d90;
    case 'CO':
      return theme.co;
    default:
      return theme.na;
  }
};

const getStatusLabel = (status) => {
  const normalized = normalizePaymentStatus(status);
  if (normalized === 'OK') return 'OK';
  if (normalized === '-' || !normalized) return '-';
  return normalized;
};

const StatusCell = ({ status, theme, dataMonth, dataYear, dataBureau }) => {
  const normalized = normalizePaymentStatus(status);
  const style = getStatusStyle(normalized, theme);
  const label = getStatusLabel(normalized);

  return (
    <div
      className="min-w-[44px] px-3 py-2 rounded-[14px] flex items-center justify-center text-[13px] font-extrabold"
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
      }}
      data-testid={`payment-cell-${dataBureau}-${dataYear}-${dataMonth}`}
      data-status={normalized}
    >
      {label}
    </div>
  );
};

export const PaymentHistoryGrid = ({
  account,
  defaultMonths = 12,
  maxMonths = 36,
  darkMode = false,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const theme = getTheme(darkMode);

  const paymentHistory = useMemo(() => parsePaymentHistory(account), [account]);
  const totalAvailableMonths = paymentHistory.allMonths.size;

  const monthColumns = useMemo(() => {
    if (paymentHistory.allMonths.size === 0) return [];
    const limit = expanded
      ? (totalAvailableMonths > maxMonths ? maxMonths : null)
      : defaultMonths;
    return generateMonthColumnsFromData(paymentHistory.allMonths, limit);
  }, [paymentHistory.allMonths, expanded, defaultMonths, maxMonths, totalAvailableMonths]);

  const hasData = BUREAUS.some((bureau) => paymentHistory[bureau].length > 0);
  const hasMoreMonths = totalAvailableMonths > monthColumns.length;

  const getStatus = (bureau, year, month) =>
    getStatusForBureauMonth(paymentHistory, bureau, year, month);

  return (
    <div
      className={cn('rounded-[20px] overflow-hidden', className)}
      style={{
        background: theme.card.bg,
        border: `1px solid ${theme.card.border}`,
        backdropFilter: 'blur(14px)',
      }}
      data-testid="payment-history-grid"
    >
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: `1px solid ${theme.lineSoft}` }}
      >
        <div className="flex items-center gap-2.5">
          <Calendar size={16} style={{ color: darkMode ? '#F0C160' : '#C98A1B' }} />
          <h4
            className="text-[15px] font-black tracking-tight"
            style={{ color: theme.text.primary, letterSpacing: '-0.02em' }}
          >
            Payment History by Bureau
          </h4>
          {totalAvailableMonths > 0 && (
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={{
                background: darkMode ? 'rgba(255,255,255,.08)' : 'rgba(17,24,39,.06)',
                color: theme.text.muted,
              }}
            >
              {monthColumns.length} of {totalAvailableMonths} months
            </span>
          )}
        </div>
        {(hasMoreMonths || expanded) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-sm font-medium"
            style={{ color: theme.text.muted }}
            data-testid="payment-history-show-more"
          >
            {expanded ? (
              <>
                <ChevronUp size={14} className="mr-1" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown size={14} className="mr-1" />
                Show More
              </>
            )}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        {hasData ? (
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: theme.header.bg }}>
                <th
                  className="sticky left-0 px-4 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider min-w-[110px]"
                  style={{
                    background: theme.header.bg,
                    color: theme.text.muted,
                    borderBottom: `1px solid ${theme.lineSoft}`,
                  }}
                >
                  Bureau
                </th>
                {monthColumns.map((col) => (
                  <th
                    key={`${col.year}-${col.month}`}
                    className="px-1 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider min-w-[56px]"
                    style={{
                      color: theme.text.muted,
                      borderBottom: `1px solid ${theme.lineSoft}`,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BUREAUS.map((bureau, idx) => (
                <tr
                  key={bureau}
                  style={{ background: idx % 2 === 0 ? theme.row.bg : theme.row.alt }}
                >
                  <td
                    className="sticky left-0 px-4 py-2.5 text-sm font-extrabold"
                    style={{
                      background: idx % 2 === 0 ? theme.row.bg : theme.row.alt,
                      color: theme.text.secondary,
                      borderBottom: `1px solid ${theme.lineSoft}`,
                    }}
                  >
                    {bureau}
                  </td>
                  {monthColumns.map((col) => (
                    <td
                      key={`${bureau}-${col.year}-${col.month}`}
                      className="px-1 py-2.5 text-center"
                      style={{ borderBottom: `1px solid ${theme.lineSoft}` }}
                    >
                      <StatusCell
                        status={getStatus(bureau, col.year, col.month)}
                        theme={theme}
                        dataBureau={bureau.toLowerCase()}
                        dataYear={col.year}
                        dataMonth={col.month}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : account?.payment_history_summary && Object.keys(account.payment_history_summary).length > 0 ? (
          <div className="p-4">
            <p className="text-sm mb-4" style={{ color: theme.text.secondary }}>
              Detailed payment history grid not available. Summary:
            </p>
            <div className="grid grid-cols-3 gap-4">
              {BUREAUS.map((bureau) => {
                const summary = account.payment_history_summary[bureau.toLowerCase()] || {};
                return (
                  <div
                    key={bureau}
                    className="rounded-xl p-3"
                    style={{
                      background: darkMode ? 'rgba(255,255,255,.03)' : 'rgba(17,24,39,.04)',
                      border: `1px solid ${theme.card.border}`,
                    }}
                  >
                    <h5 className="font-bold text-sm mb-2" style={{ color: theme.text.secondary }}>{bureau}</h5>
                    {summary.has_history ? (
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span style={{ color: theme.ok.color }}>On-time payments:</span>
                          <span className="font-medium" style={{ color: theme.text.primary }}>{summary.ok_payments || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span style={{ color: theme.d30.color }}>Late payments:</span>
                          <span className="font-medium" style={{ color: theme.text.primary }}>{summary.late_payments || 0}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs" style={{ color: theme.text.muted }}>No history</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center" data-testid="payment-history-empty">
            <Calendar size={24} className="mx-auto mb-2" style={{ color: theme.text.muted }} />
            <p className="text-sm" style={{ color: theme.text.muted }}>No payment history available</p>
          </div>
        )}
      </div>

      <div
        className="p-4 flex flex-wrap items-center gap-2.5"
        style={{
          background: theme.legend.bg,
          borderTop: `1px solid ${theme.lineSoft}`,
        }}
      >
        <span className="text-[13px] font-bold mr-1" style={{ color: theme.text.muted }}>Legend:</span>
        {[
          ['OK', theme.ok, 'Current'],
          ['30', theme.d30, '30 Days Late'],
          ['60', theme.d60, '60 Days'],
          ['90', theme.d90, '90 Days'],
          ['CO', theme.co, 'Charge-Off'],
          ['-', theme.na, 'No Data'],
        ].map(([code, style, label]) => (
          <div key={code} className="flex items-center gap-1.5">
            <div
              className="min-w-[44px] px-3 py-2 rounded-[14px] flex items-center justify-center text-[13px] font-extrabold"
              style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color }}
            >
              {code}
            </div>
            <span className="text-[13px]" style={{ color: theme.text.secondary }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentHistoryGrid;
